import React from "react";
import { Models } from "@hex-insights/app-modules";
import { addTimeToDate, Button, dateTrunc, makeClassName, Row, Tooltip, useUpdateEffect } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	DateTimeField,
	DateTimeFieldInputProps,
	DateTimeInput,
	FileUploadField,
	FormTooltipButton,
	FormType,
	SubFormField,
	SubFormRenderProps,
	TextField,
} from "@hex-insights/forms";
import { RichTextDisplay, RichTextInput, stripHTMLFromString } from "../../../Components";
import { NewsletterDetailQuery } from "../../../Utilities";
import { EditSharingMenu, NestedCreateSharingMenu } from "../../Components/SharingPolicy";
import { NewsletterFormValues, SharingPolicyCustomFormValues } from "../../FormUtilities";
import { BaseFieldProps } from "../shared";
import { UserSelectField } from "../User";
import styles from "./field.module.css";

/**
 * Generic props for fields of the Newsletter model.
 */
type FieldProps<K extends keyof NewsletterFormValues.Base = keyof NewsletterFormValues.Base> = BaseFieldProps<
	Pick<NewsletterFormValues.Base, K>
>;

/**
 * Generic props for fields of the Newsletter model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof NewsletterFormValues.Create = keyof NewsletterFormValues.Create> = BaseFieldProps<
	Pick<NewsletterFormValues.Create, K>
>;

/**
 * Generic props for fields of the Newsletter model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof NewsletterFormValues.Detail = keyof NewsletterFormValues.Detail> = BaseFieldProps<
	Pick<NewsletterFormValues.Detail, K>
>;

/**
 * Field component for the `title` field of the Newsletter model.
 */
export function Title({ formState, className, ...props }: FieldProps<"title">) {
	return (
		<TextField formState={formState} name="title" className={makeClassName(styles["title"], className)} {...props} />
	);
}

/**
 * Field component for the `image` field of the Newsletter model.
 */
export function Image({
	formState,
	url,
	className,
	...props
}: FieldProps<"imageKey" | "imageContentType"> & { url?: string }) {
	Models.requireURLForUpdateForm(url, false, formState.formType, "Image");

	return (
		<FileUploadField
			formState={formState}
			keyFieldName="imageKey"
			contentTypeFieldName="imageContentType"
			url={url}
			optional
			noOptional
			imagePreviewSizing="cover"
			className={makeClassName(styles["image"], className)}
			{...props}
		/>
	);
}

/**
 * Field component for the `body` field of the Newsletter model.
 */
export function Body({ formState, className, ...props }: FieldProps<"body">) {
	return (
		<TextField
			formState={formState}
			name="body"
			multiLine
			Input={RichTextInput}
			format={stripHTMLFromString}
			display={RichTextDisplay}
			className={makeClassName(styles["body"], className)}
			{...props}
		/>
	);
}

/**
 * Field component for the `releaseAt` field of the Newsletter model.
 */
export function ReleaseAt({ formState, ...props }: FieldProps<"releaseAt">) {
	return (
		<DateTimeField
			formState={formState}
			name="releaseAt"
			optional
			precision="minute"
			{...props}
			Input={FormType.isCreate(formState.formType) ? ReleaseAtInput : undefined}
			noOptional
		/>
	);
}

function ReleaseAtInput({ setValue, ...props }: DateTimeFieldInputProps) {
	const [preset, setPreset] = React.useState<"not-yet" | "now" | "tomorrow" | "custom">("not-yet");

	useUpdateEffect(() => {
		switch (preset) {
			case "not-yet":
				setValue(null);
				break;
			case "now":
				setValue(new Date().toISOString());
				break;
			case "tomorrow":
				setValue(addTimeToDate(dateTrunc(addTimeToDate(new Date(), [1, "day"]), "day"), [6, "hours"]).toISOString());
				break;
		}
	}, [preset, setValue]);

	return (
		<Row justify="spaced-start" horizontalSpacing="0.5rem" className={styles["start-time-input"]}>
			<Button
				variant={preset === "not-yet" ? "primary" : "tertiary"}
				onClick={() => setPreset("not-yet")}
				className="nowrap"
			>
				Not Yet
			</Button>
			<Button variant={preset === "now" ? "primary" : "tertiary"} onClick={() => setPreset("now")}>
				Now
			</Button>
			<Button variant={preset === "tomorrow" ? "primary" : "tertiary"} onClick={() => setPreset("tomorrow")}>
				Tomorrow
			</Button>
			<FormTooltipButton formState={{} as any} autoFocus>
				{() => (
					<Tooltip isInteractable>
						<Tooltip.Body>
							<DateTimeInput setValue={setValue} {...props} style={{ width: "var(--date-time-field---width)" }} />
						</Tooltip.Body>
					</Tooltip>
				)}
				{({ onClick }) => (
					<Button
						variant={preset === "custom" ? "primary" : "tertiary"}
						onClick={() => {
							onClick();
							setPreset("custom");
						}}
					>
						Custom
					</Button>
				)}
			</FormTooltipButton>
		</Row>
	);
}

export namespace Author {
	export type SelectProps = DetailFieldProps<"authorID"> &
		Omit<UserSelectField.SelectProps<NewsletterFormValues.Detail>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `author` edge of the Newsletter model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="authorID" optional {...props} />;
	}
}

export namespace SharingPolicy {
	/**
	 * SubForm field component for the `sharingPolicy` edge of the Newsletter model.
	 */
	export function SubForm({ formState, noHeader = true, className, ...props }: CreateFieldProps<"sharingPolicy">) {
		return (
			<SubFormField
				formState={formState}
				name="sharingPolicy"
				blankItem={SharingPolicyCustomFormValues.blankItemInParent}
				minItems={1}
				maxItems={1}
				formattedBlank=""
				noHeader={noHeader}
				className={makeClassName("sharing-record-sub-form-field", className)}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({ formState }: SubFormRenderProps<SharingPolicyCustomFormValues.CreateInParent>) {
		return <NestedCreateSharingMenu formState={formState} />;
	};

	SubForm.AddButton = function (
		_: AddSubFormButtonRenderProps<
			Pick<NewsletterFormValues.Create, "sharingPolicy">,
			SharingPolicyCustomFormValues.CreateInParent
		>,
	) {
		return <React.Fragment></React.Fragment>;
	};

	export type DetailProps = {
		sharingPolicy: NewsletterDetailQuery["newsletter"]["sharingPolicy"];
	};

	export function Detail({ sharingPolicy }: DetailProps) {
		if (!sharingPolicy) {
			return null;
		}

		return <EditSharingMenu sharingPolicy={sharingPolicy} />;
	}
}
